// react
import { ChangeEvent, ReactNode, useState } from 'react';

// types
import { FlagCode } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Grid, InputLabel, SelectChangeEvent, Typography } from '@mui/material';

// fields
import { Input, InputProps } from '../Input/Input';
import { Select } from '../Select/Select';

// ui
import { Flag } from '../../ui/Flag/Flag';

// utils
import { countries } from '../../../utils/countries';

// props
export interface InputPhoneValue {
	country: {
		code: string;
		smsPrefix: number;
	};
	sms: string;
}

export interface InputPhoneProps extends Omit<InputProps, 'onChange' | 'variant'> {
	defaultCountry?: string;
	defaultValue?: string;
	onChange?: (
		e: SelectChangeEvent<unknown> | ChangeEvent<HTMLInputElement>,
		value: InputPhoneValue
	) => void;
	variant?: 'merged' | 'separated';
}

export const InputPhone = ({
	defaultCountry = '',
	defaultValue = '',
	disabled,
	label,
	onChange,
	required,
	variant = 'merged',
	...props
}: InputPhoneProps) => {
	// hooks
	const { t } = useTranslation();

	// defaults
	const defaultValues = {
		country: defaultCountry,
		sms: defaultValue,
	};

	// state
	const [values, setValues] = useState(defaultValues);

	// options
	const optionsCountries = countries.map((country) => {
		return {
			icon: <Flag code={country.code as FlagCode} />,
			label: (
				<Grid container spacing={1}>
					<Grid item>
						<Typography>{country.name}</Typography>
					</Grid>
					{country.smsPrefix && (
						<Grid item>
							<Typography color="brand.grey.500">+{country.smsPrefix}</Typography>
						</Grid>
					)}
				</Grid>
			),
			smsPrefix: country.smsPrefix,
			value: String(country.code),
		};
	});

	const handleChangeCountry = (e: SelectChangeEvent<unknown>, child: ReactNode) => {
		const { value } = e.target as HTMLInputElement;
		const option = optionsCountries.find((option) => option.value === value);

		if (onChange) {
			onChange(e, {
				country: {
					code: option?.value || '',
					smsPrefix: option?.smsPrefix || 0,
				},
				sms: values.sms,
			});
		}

		setValues((prev) => ({
			...prev,
			country: value,
		}));
	};

	const handleChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target as HTMLInputElement;
		const option = optionsCountries.find((option) => option.value === values.country);

		if (onChange) {
			onChange(e, {
				country: {
					code: option?.value || '',
					smsPrefix: option?.smsPrefix || 0,
				},
				sms: value,
			});
		}

		setValues((prev) => ({
			...prev,
			sms: value,
		}));
	};

	return (
		<Grid container>
			{label && (
				<Grid item xs={12}>
					<InputLabel disabled={disabled} required={required}>
						{label}
					</InputLabel>
				</Grid>
			)}
			<Grid item xs={12}>
				<Grid container spacing={variant === 'separated' ? 2 : 0}>
					<Grid item xs="auto">
						<Select
							disabled={disabled}
							hasNoneOption={false}
							onChange={handleChangeCountry}
							options={optionsCountries}
							renderValue={(value) => {
								const option = optionsCountries.find((option) => option.value === value);
								if (option) {
									return (
										<Grid container spacing={1}>
											<Grid item>{option.icon || t('na')}</Grid>
											{option.smsPrefix && <Grid item>+{option.smsPrefix}</Grid>}
										</Grid>
									);
								}
								return value;
							}}
							required={required}
							sx={(theme) => {
								if (variant === 'merged') {
									return {
										borderBottomRightRadius: `0 !important`,
										borderTopRightRadius: `0 !important`,
										'&.MuiInputBase-root:hover, &.Mui-focused': {
											'& fieldset': {
												borderColor: `${theme.palette.divider} !important`,
											},
										},
										'& fieldset': {
											borderRight: 'none !important',
										},
									};
								}
								return {};
							}}
							value={values.country}
						/>
					</Grid>
					<Grid item xs={true}>
						<Input
							disabled={disabled}
							id="sms"
							name="sms"
							{...props}
							InputProps={{
								sx: (theme) => {
									if (variant === 'merged') {
										return {
											borderBottomLeftRadius: `0 !important`,
											borderTopLeftRadius: `0 !important`,
											'& input': {
												paddingLeft: 0,
											},
											'&.MuiInputBase-root:hover, &.Mui-focused': {
												'& fieldset': {
													borderColor: `${theme.palette.divider} !important`,
												},
											},
											'& fieldset': {
												borderLeft: 'none !important',
											},
										};
									}

									return {};
								},
							}}
							onChange={handleChangePhone}
							required={required}
							type="tel"
							value={values.sms}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
